import Button from "@components/ui/button";
import {
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { cn } from "@lib/cn";
import { IconPlus } from "@tabler/icons-react";
import * as React from "react";
import {
  buildDateToken,
  DATE_FORMAT_OPTIONS,
  DATE_UNIT_OPTIONS,
  defaultDateFormatFor,
  parseDateToken,
  type DateOffsetUnit,
} from "../date-token";
import { OptionSelect } from "./option-select";

export interface DateVariablePopoverProps {
  anchorEl: HTMLElement | null;
  /**
   * Re-resolves the current live pill element (e.g. by index) after a
   * re-render replaces the original node. Needed so the popover tracks the
   * pill on scroll — the captured `anchorRect` is only a static fallback.
   */
  resolveAnchorEl?: () => HTMLElement | null;
  /**
   * Pill rect captured at click time. Used as the position reference (the live
   * pill can measure as a 0,0 rect after a re-render, pinning the popover to
   * the top-left corner).
   */
  anchorRect?: DOMRect | null;
  /** Current pill token name (`name[±Nd|m|y][|format]`). */
  token: string;
  onClose: () => void;
  /** Called with the rewritten token name when the user saves changes. */
  onSave: (nextToken: string) => void;
}

// The name's own default leads the list when it isn't one of the shared
// options (month/year print `MMMM` / `YYYY`), so the select never opens blank.
const formatOptionsFor = (name: string) => {
  const fallback = defaultDateFormatFor(name);
  const formats: readonly string[] = DATE_FORMAT_OPTIONS;
  return (formats.includes(fallback) ? formats : [fallback, ...formats]).map(
    (format) => ({ value: format, label: format }),
  );
};

const UNIT_OPTIONS = DATE_UNIT_OPTIONS.map(({ value, label }) => ({
  value,
  label,
}));

export const DateVariablePopover = ({
  anchorEl,
  resolveAnchorEl,
  anchorRect,
  token,
  onClose,
  onSave,
}: DateVariablePopoverProps) => {
  const parsed = React.useMemo(() => parseDateToken(token), [token]);
  const formatOptions = React.useMemo(
    () => formatOptionsFor(parsed.name),
    [parsed.name],
  );

  const [format, setFormat] = React.useState(
    parsed.format ?? defaultDateFormatFor(parsed.name),
  );
  const [amount, setAmount] = React.useState(
    String(parsed.offset?.amount ?? 0),
  );
  const [unit, setUnit] = React.useState<DateOffsetUnit>(
    parsed.offset?.unit ?? "d",
  );

  const open = !!(anchorEl || anchorRect);

  const { refs, floatingStyles, context, update, isPositioned } = useFloating({
    open,
    onOpenChange: (next) => {
      if (!next) onClose();
    },
    placement: "top-start",
    strategy: "fixed",
    middleware: [
      offset(8),
      flip({ fallbackPlacements: ["bottom-start"], padding: 8 }),
      shift({ padding: 8 }),
    ],
  });

  // The current connected pill (re-resolved after re-renders), or null when it
  // isn't measurable — used so autoUpdate can track scroll.
  const reference = React.useMemo<Element | null>(() => {
    const current = resolveAnchorEl?.() ?? anchorEl;
    if (current?.isConnected) {
      const rect = current.getBoundingClientRect();
      if (rect.width > 0 || rect.height > 0) return current;
    }
    return null;
  }, [anchorEl, resolveAnchorEl]);

  // Prefer the live (re-resolved) pill rect, re-read every animation frame so
  // the popover follows scroll; fall back to the click-time rect when the pill
  // isn't measurable (avoids the top-left pinning).
  const positionReference = React.useMemo(() => {
    if (!reference && !anchorRect) return null;
    return {
      getBoundingClientRect: () => {
        const current = resolveAnchorEl?.() ?? anchorEl;
        if (current?.isConnected) {
          const live = current.getBoundingClientRect();
          if (live.width > 0 || live.height > 0) return live;
        }
        return anchorRect ?? new DOMRect(0, 0, 0, 0);
      },
      contextElement: anchorEl ?? undefined,
    };
  }, [anchorEl, anchorRect, reference, resolveAnchorEl]);

  React.useLayoutEffect(() => {
    if (!positionReference) {
      refs.setReference(null);
      return;
    }
    refs.setReference(reference ?? positionReference);
    refs.setPositionReference(positionReference);
    update();
  }, [positionReference, reference, refs, update]);

  // Re-measure every animation frame while open. autoUpdate's change-detection
  // doesn't reliably track the re-resolved pill in the inactive text path (the
  // pill node is recreated on re-render) or transform-based canvas panning, so
  // we drive `update()` directly — it re-reads the live pill rect through the
  // position reference each frame, keeping the popover glued to the pill.
  React.useEffect(() => {
    if (!open) return;
    let raf = 0;
    const loop = () => {
      update();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [open, update]);

  const dismiss = useDismiss(context, { outsidePressEvent: "mousedown" });
  const role = useRole(context);
  const { getFloatingProps } = useInteractions([dismiss, role]);

  const handleSave = () => {
    const numeric = Number.parseInt(amount, 10);
    const safeAmount = Number.isFinite(numeric) ? numeric : 0;
    onSave(
      buildDateToken({
        name: parsed.name,
        offset: safeAmount === 0 ? null : { amount: safeAmount, unit },
        format,
      }),
    );
    onClose();
  };

  if (!open) return null;

  return (
    <FloatingPortal>
      <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
        <div
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            visibility: isPositioned ? "visible" : "hidden",
          }}
          {...getFloatingProps()}
          className={cn(
            "tw-z-[999999] tw-flex tw-w-[264px] tw-flex-col tw-gap-4 tw-rounded-xl tw-bg-white tw-p-4",
            "tw-shadow-xs tw-outline tw-outline-1 tw-outline-blue/5",
          )}
        >
          <div className="tw-flex tw-flex-col tw-gap-3">
            <span className="tw-text-sm tw-font-medium tw-leading-5 tw-text-blue">
              Format
            </span>
            <OptionSelect
              options={formatOptions}
              value={format}
              onChange={setFormat}
              placeholder="Select format"
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-3">
            <span className="tw-text-sm tw-font-medium tw-leading-5 tw-text-blue">
              Calculation
            </span>
            <div className="tw-flex tw-items-start tw-gap-2">
              <div className="tw-flex tw-h-9 tw-flex-1 tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-blue-100 tw-bg-white tw-px-3">
                <IconPlus
                  size={16}
                  stroke={1.5}
                  className="tw-shrink-0 tw-text-blue-500"
                />
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="tw-bg-transparent tw-w-full tw-border-none tw-p-0 tw-font-sans tw-text-sm tw-text-blue tw-outline-none"
                />
              </div>
              <OptionSelect
                className="tw-w-[88px]"
                options={UNIT_OPTIONS}
                value={unit}
                onChange={(value) => setUnit(value as DateOffsetUnit)}
                placeholder="days"
              />
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
            <Button variant="secondary" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="default" size="sm" onClick={handleSave}>
              Save changes
            </Button>
          </div>
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
};

export default DateVariablePopover;
